import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Accordion,
  Card,
  Grid,
  Group,
  Stack,
  Text,
  Title,
  Badge,
  Loader,
  Center,
  Alert,
  Button,
  Divider,
  Tooltip,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconAlertCircle,
  IconArrowDown,
  IconArrowUp,
  IconRefresh,
  IconSend,
} from '@tabler/icons-react';
import { depositsApi, type ApiAuditEntry } from '../api/deposits';
import type { ManualStatusUpdateResponse, TrxStatus } from '../api/types';
import { formatGmt7, formatIdr, statusColor } from '../lib/format';
import { DetailField } from '../components/DetailField';
import { JsonView } from '../components/JsonView';
import { ManualStatusControl } from '../components/ManualStatusControl';
import { ApiCallError, describeError } from '../api/client';
import { usePermissions, useSession } from '../auth/SessionContext';
import { isPlatform } from '../auth/permissions';
import { useTranslation } from '../i18n/LocaleContext';

export function DepositDetailPage() {
  const { trxId = '' } = useParams<{ trxId: string }>();
  const { t } = useTranslation();
  const { has } = usePermissions();
  const { state: sessionState } = useSession();
  const queryClient = useQueryClient();
  const [resendNotice, setResendNotice] = useState<
    { kind: 'success'; deliveryId: string; targetUrl: string } | null
  >(null);
  const [statusNotice, setStatusNotice] = useState<ManualStatusUpdateResponse | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['deposit', trxId],
    queryFn: () => depositsApi.detail(trxId),
    enabled: trxId.length > 0,
    retry: 0,
  });
  const auditQuery = useQuery({
    queryKey: ['deposit-audit-log', trxId],
    queryFn: () => depositsApi.apiLog(trxId),
    enabled: trxId.length > 0,
    retry: 0,
  });

  // Either retry permission unlocks the button — server decides which one is
  // actually required based on the viewer's userType, so we only need to know
  // that the viewer has *some* retry capability.
  const canResend = has('webhook.delivery.retry') || has('webhook.delivery.internal.retry');

  const resendMut = useMutation({
    meta: {
      successKey: 'notify.webhook.resend.ok',
      errorKey: 'notify.webhook.resend.fail',
    },
    mutationFn: () => depositsApi.resendWebhook(trxId),
    onSuccess: (resp) => {
      setResendNotice({ kind: 'success', deliveryId: resp.deliveryId, targetUrl: resp.targetUrl });
      // Audit log + new outbox row → refresh API activity so the new attempt
      // appears once the worker delivers it.
      void queryClient.invalidateQueries({ queryKey: ['deposit-audit-log', trxId] });
    },
  });

  // Manual status override: internal staff only (the server enforces the same
  // rule — a merchant must never be able to mark its own deposit paid).
  const canUpdateStatus =
    sessionState.status === 'authenticated' &&
    isPlatform(sessionState.profile) &&
    has('deposits.status.update');

  const statusMut = useMutation({
    meta: {
      successKey: 'notify.trx.status.ok',
      errorKey: 'notify.trx.status.fail',
    },
    mutationFn: (input: { status: TrxStatus; resendCallback: boolean }) =>
      depositsApi.updateStatus(trxId, input),
    onSuccess: (resp) => {
      setStatusNotice(resp);
      setResendNotice(null);
      void queryClient.invalidateQueries({ queryKey: ['deposit', trxId] });
      void queryClient.invalidateQueries({ queryKey: ['deposit-audit-log', trxId] });
      void queryClient.invalidateQueries({ queryKey: ['deposits'] });
    },
  });

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="sm">
          <Button component={Link} to="/dashboard/deposits" variant="default" leftSection={<IconArrowLeft size={14} />}>
            {t('common.back')}
          </Button>
          <Title order={3}>{t('depositDetail.title')}</Title>
        </Group>
        {(canUpdateStatus || canResend) && (
          <Group gap="sm" wrap="wrap">
            {canUpdateStatus && (
              <ManualStatusControl
                trxId={trxId}
                currentStatus={data?.transpay.trxStatus}
                loading={statusMut.isPending}
                onConfirm={(input) => statusMut.mutate(input)}
              />
            )}
            {/* Separator: the manual override (moves money) is a different
                kind of action from re-sending an already-emitted webhook. */}
            {canUpdateStatus && canResend && <Divider orientation="vertical" />}
            {canResend && (
              <Tooltip
                label={
                  data?.transpay.trxStatus === 'paid' || data?.transpay.trxStatus === 'failed'
                    ? t('depositDetail.resendTooltipReady')
                    : t('depositDetail.resendTooltipNotReady')
                }
              >
                <Button
                  variant="default"
                  leftSection={<IconSend size={14} />}
                  onClick={() => resendMut.mutate()}
                  loading={resendMut.isPending}
                  disabled={
                    !data ||
                    (data.transpay.trxStatus !== 'paid' && data.transpay.trxStatus !== 'failed')
                  }
                >
                  {t('depositDetail.resendButton')}
                </Button>
              </Tooltip>
            )}
          </Group>
        )}
      </Group>

      {statusNotice && (
        <Alert
          color={statusNotice.callbackRequested && !statusNotice.callbackQueued ? 'yellow' : 'green'}
          icon={<IconAlertCircle size={16} />}
          withCloseButton
          onClose={() => setStatusNotice(null)}
          variant="light"
        >
          {t('trxStatus.noticeChanged', {
            from: statusNotice.previousStatus,
            to: statusNotice.status,
          })}{' '}
          {statusNotice.callbackQueued
            ? t('trxStatus.noticeCallbackQueued', {
                targetUrl: statusNotice.targetUrl ?? '',
                outboxId: statusNotice.outboxId ?? '',
              })
            : statusNotice.callbackSkippedReason === 'no_event_for_pending'
              ? t('trxStatus.noticeCallbackSkippedPending')
              : statusNotice.callbackSkippedReason === 'webhook_url_not_configured'
                ? t('trxStatus.noticeCallbackSkippedNoUrl')
                : t('trxStatus.noticeCallbackNotRequested')}
        </Alert>
      )}

      {resendNotice && (
        <Alert
          color="green"
          icon={<IconAlertCircle size={16} />}
          withCloseButton
          onClose={() => setResendNotice(null)}
          variant="light"
        >
          {t('depositDetail.resendNotice', {
            targetUrl: resendNotice.targetUrl,
            deliveryId: resendNotice.deliveryId,
          })}
        </Alert>
      )}

      {isLoading && (
        <Center py="xl">
          <Loader />
        </Center>
      )}

      {error && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          {describeError(error)}
        </Alert>
      )}

      {data && (
        <>
          <Card>
            <Stack gap="md">
              <Group justify="space-between">
                <Group gap="sm">
                  <Badge color={statusColor(data.transpay.trxStatus)} variant="filled" size="lg">
                    {data.transpay.trxStatus}
                  </Badge>
                  <Text size="sm" ff="monospace" c="dimmed">
                    {data.transpay.trxId}
                  </Text>
                </Group>
                <Text fw={700} size="xl">
                  {formatIdr(data.transpay.amount)}
                </Text>
              </Group>

              <Divider />

              <Grid>
                <Grid.Col span={{ base: 6, md: 4 }}>
                  <DetailField label={t('depositDetail.fieldReference')}>{data.transpay.referenceId}</DetailField>
                </Grid.Col>
                <Grid.Col span={{ base: 6, md: 4 }}>
                  <DetailField label={t('depositDetail.fieldMerchant')}>
                    {data.transpay.merchantName} (id={data.transpay.merchantId})
                  </DetailField>
                </Grid.Col>
                <Grid.Col span={{ base: 6, md: 4 }}>
                  <DetailField label={t('depositDetail.fieldMethod')}>{data.transpay.method}</DetailField>
                </Grid.Col>
                <Grid.Col span={{ base: 6, md: 4 }}>
                  <DetailField label={t('depositDetail.fieldFee')}>{formatIdr(data.transpay.fee)}</DetailField>
                </Grid.Col>
                <Grid.Col span={{ base: 6, md: 4 }}>
                  <DetailField label={t('depositDetail.fieldMerchantRevenue')}>{formatIdr(data.transpay.merchantRevenue)}</DetailField>
                </Grid.Col>
                <Grid.Col span={{ base: 6, md: 4 }}>
                  <DetailField label={t('depositDetail.fieldFromIp')}>{data.transpay.fromIp}</DetailField>
                </Grid.Col>
                <Grid.Col span={{ base: 6, md: 4 }}>
                  <DetailField label={t('depositDetail.fieldPayer')}>{data.transpay.payersName}</DetailField>
                </Grid.Col>
                <Grid.Col span={{ base: 6, md: 4 }}>
                  <DetailField label={t('depositDetail.fieldRrn')}>{data.transpay.rrnNumber}</DetailField>
                </Grid.Col>
                <Grid.Col span={{ base: 6, md: 4 }}>
                  <DetailField label={t('depositDetail.fieldCreated')}>{formatGmt7(data.transpay.createdAt)}</DetailField>
                </Grid.Col>
                <Grid.Col span={{ base: 6, md: 4 }}>
                  <DetailField label={t('depositDetail.fieldPaid')}>{formatGmt7(data.transpay.paidAt)}</DetailField>
                </Grid.Col>
                {data.transpay.trxStatus === 'paid' && (
                  <Grid.Col span={{ base: 6, md: 4 }}>
                    <DetailField
                      label={
                        data.transpay.settledAt ? t('depositDetail.fieldSettled') : t('depositDetail.fieldScheduledSettlement')
                      }
                    >
                      {data.transpay.settledAt ? (
                        <Group gap={6}>
                          <Badge color="green" variant="light" size="sm">{t('depositDetail.badgeSettled')}</Badge>
                          <Text size="sm">{formatGmt7(data.transpay.settledAt)}</Text>
                        </Group>
                      ) : data.transpay.settleAt ? (
                        <Group gap={6}>
                          <Badge color="yellow" variant="light" size="sm">{t('depositDetail.badgeAwaiting')}</Badge>
                          <Text size="sm">{formatGmt7(data.transpay.settleAt)}</Text>
                        </Group>
                      ) : (
                        <Text size="sm" c="dimmed">—</Text>
                      )}
                    </DetailField>
                  </Grid.Col>
                )}
                {data.transpay.remark && (
                  <Grid.Col span={12}>
                    <DetailField label={t('depositDetail.fieldRemark')}>{data.transpay.remark}</DetailField>
                  </Grid.Col>
                )}
              </Grid>
            </Stack>
          </Card>

          {/* Legacy "PAYOK audit" snapshot intentionally removed — it only
              ever held the LAST callback payload. The API Activity timeline
              below covers the full history (one row per callback / retry)
              with response times. The PAYOK order identifiers can be read
              from the timeline's outbound entry. */}

          <ApiActivitySection
            trxId={data.transpay.trxId}
            payokMerchantOrderId={data.payok?.merchantOrderId ?? null}
            payokPlatformOrderId={data.payok?.platformOrderId ?? null}
            isLoading={auditQuery.isLoading}
            error={auditQuery.error}
            data={auditQuery.data}
            onRefresh={() => auditQuery.refetch()}
            refreshing={auditQuery.isRefetching}
          />
        </>
      )}
    </Stack>
  );
}

/**
 * Activity timeline organised around the transaction lifecycle.
 *
 * The shape adapts to the viewer's permissions:
 *
 *   Internal viewers (canSeePayok=true):
 *     - Initial Request → cashin in + upstream order create out
 *     - Notification Cycles → one card per upstream callback, with its
 *       downstream merchant-webhook attempts attached.
 *
 *   External (merchant) viewers (canSeePayok=false):
 *     - Initial Request → cashin in only
 *     - Webhook Notifications → one card per `x-transpay-delivery-id`,
 *       with its retries attached. The upstream provider is never mentioned.
 *
 * Two grouping strategies behind the same data structure.
 */
type LifecycleData = {
  initial: {
    merchantIn: ApiAuditEntry | null;
    payokOut: ApiAuditEntry | null;
  };
  cycles: Array<{
    label: string;
    /** Upstream callback row (only present for internal viewers). */
    callback: ApiAuditEntry | null;
    webhookAttempts: ApiAuditEntry[]; // TransPay → Merchant outbound, in order
  }>;
  /** Anything we couldn't categorise — shouldn't happen in practice. */
  orphans: ApiAuditEntry[];
};

function getDeliveryId(entry: ApiAuditEntry): string | null {
  const headers = entry.requestHeaders as Record<string, unknown> | null;
  if (!headers) return null;
  const v = headers['x-transpay-delivery-id'];
  if (typeof v === 'string' && v.length > 0) return v;
  if (Array.isArray(v) && typeof v[0] === 'string') return v[0];
  return null;
}

function organiseLifecycle(
  rows: readonly ApiAuditEntry[],
  canSeePayok: boolean,
): LifecycleData {
  // Sort ascending so we walk the timeline in story order.
  const asc = [...rows].sort(
    (a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime(),
  );

  const merchantIn = asc.find((r) => r.direction === 'inbound' && r.party === 'merchant') ?? null;
  const payokOut = asc.find((r) => r.direction === 'outbound' && r.party === 'payok') ?? null;
  const payokCallbacks = asc.filter((r) => r.direction === 'inbound' && r.party === 'payok');
  const merchantWebhooks = asc.filter((r) => r.direction === 'outbound' && r.party === 'merchant');

  let cycles: LifecycleData['cycles'];
  const orphans: ApiAuditEntry[] = [];

  if (canSeePayok && payokCallbacks.length > 0) {
    // Internal viewer: cycles are anchored by upstream callbacks. Each merchant
    // webhook attempt attaches to the most recent preceding callback.
    cycles = payokCallbacks.map((callback, idx) => ({
      label: `Cycle #${idx + 1}`,
      callback,
      webhookAttempts: [] as ApiAuditEntry[],
    }));
    for (const wh of merchantWebhooks) {
      const whT = new Date(wh.startedAt).getTime();
      let cycleIdx = -1;
      for (let i = 0; i < payokCallbacks.length; i += 1) {
        const cb = payokCallbacks[i]!;
        if (new Date(cb.startedAt).getTime() <= whT) cycleIdx = i;
        else break;
      }
      if (cycleIdx >= 0) cycles[cycleIdx]!.webhookAttempts.push(wh);
      else cycles[0]!.webhookAttempts.push(wh);
    }
  } else {
    // External viewer or no upstream callbacks visible: bucket merchant
    // webhook attempts by `x-transpay-delivery-id`. One delivery-id = one
    // "notification event"; multiple rows under the same id are retries.
    const byDelivery = new Map<string, ApiAuditEntry[]>();
    const unknown: ApiAuditEntry[] = [];
    for (const wh of merchantWebhooks) {
      const id = getDeliveryId(wh);
      if (id == null) {
        unknown.push(wh);
        continue;
      }
      const bucket = byDelivery.get(id) ?? [];
      bucket.push(wh);
      byDelivery.set(id, bucket);
    }
    // Order delivery groups by the first attempt's startedAt so the UI reads
    // in the order events actually happened.
    const orderedKeys = [...byDelivery.keys()].sort((a, b) => {
      const at = new Date(byDelivery.get(a)![0]!.startedAt).getTime();
      const bt = new Date(byDelivery.get(b)![0]!.startedAt).getTime();
      return at - bt;
    });
    cycles = orderedKeys.map((id, idx) => ({
      label: `Notification #${idx + 1}`,
      callback: null,
      webhookAttempts: byDelivery.get(id)!,
    }));
    if (unknown.length > 0) {
      // Webhooks without a delivery-id (shouldn't happen after the recorder
      // change, but defensive) become a final pseudo-cycle so they're visible.
      cycles.push({
        label: `Notification #${cycles.length + 1}`,
        callback: null,
        webhookAttempts: unknown,
      });
    }
  }

  return {
    initial: { merchantIn, payokOut: canSeePayok ? payokOut : null },
    cycles,
    orphans,
  };
}

/**
 * Pick a color for the duration badge based on how slow the call was. Tuned
 * for our payment flow: <200ms = green ✓, <500ms = blue, <2s = yellow,
 * <5s = orange, >=5s = red (this is bad and we want it loud).
 */
function durationColor(ms: number | null): string {
  if (ms == null) return 'gray';
  if (ms < 200) return 'green';
  if (ms < 500) return 'blue';
  if (ms < 2000) return 'yellow';
  if (ms < 5000) return 'orange';
  return 'red';
}

function ApiActivitySection({
  trxId,
  payokMerchantOrderId,
  payokPlatformOrderId,
  isLoading,
  error,
  data,
  onRefresh,
  refreshing,
}: {
  trxId: string;
  payokMerchantOrderId: string | null;
  payokPlatformOrderId: string | null;
  isLoading: boolean;
  error: unknown;
  data: { rows: ApiAuditEntry[]; canSeeMerchant: boolean; canSeePayok: boolean } | undefined;
  onRefresh: () => void;
  refreshing: boolean;
}) {
  const { t } = useTranslation();
  // 403 from the endpoint means viewer has neither perm — don't render anything.
  if (error instanceof ApiCallError && error.code === 'permission_denied') {
    return null;
  }

  const canSeePayok = data?.canSeePayok ?? false;
  const lifecycle = data ? organiseLifecycle(data.rows, canSeePayok) : null;
  const firstStartedAt = data?.rows.length ? minStartedAt(data.rows) : null;
  const hasSynthesised = data?.rows.some((r) => r.synthesised) ?? false;

  return (
    <Card>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={4}>{t('apiActivity.title')}</Title>
            <Text
              size="xs"
              c="dimmed"
              dangerouslySetInnerHTML={{
                __html: canSeePayok
                  ? t('apiActivity.descInternalDeposit')
                  : t('apiActivity.descExternalDeposit'),
              }}
            />
          </div>
          <Tooltip label={t('apiActivity.reloadTooltip')}>
            <Button
              size="xs"
              variant="default"
              leftSection={<IconRefresh size={14} />}
              loading={refreshing}
              onClick={onRefresh}
            >
              {t('apiActivity.refresh')}
            </Button>
          </Tooltip>
        </Group>

        {/* Upstream identifiers (merchantOrderId / platformOrderId) are
            internal-only details — they identify the trx on the upstream
            provider's side. Hidden from merchant users. */}
        {canSeePayok && (payokMerchantOrderId || payokPlatformOrderId) && (
          <Group gap="xl">
            {payokMerchantOrderId && (
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{t('apiActivity.upstreamMerchantOrderId')}</Text>
                <Text size="xs" ff="monospace">{payokMerchantOrderId}</Text>
              </div>
            )}
            {payokPlatformOrderId && (
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{t('apiActivity.upstreamPlatformOrderId')}</Text>
                <Text size="xs" ff="monospace">{payokPlatformOrderId}</Text>
              </div>
            )}
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{t('apiActivity.trxId')}</Text>
              <Text size="xs" ff="monospace">{trxId}</Text>
            </div>
          </Group>
        )}
        {!canSeePayok && (
          <Group gap="xl">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{t('apiActivity.trxId')}</Text>
              <Text size="xs" ff="monospace">{trxId}</Text>
            </div>
          </Group>
        )}

        {data && (
          <Group gap="xs">
            <Badge color={data.canSeeMerchant ? 'brand' : 'gray'} variant={data.canSeeMerchant ? 'light' : 'outline'}>
              {data.canSeeMerchant ? t('apiActivity.merchantRowsVisible') : t('apiActivity.merchantRowsHidden')}
            </Badge>
            {/* Only show the upstream-rows badge to viewers who can actually
                see them — otherwise the merchant would learn that some hidden
                provider exists. */}
            {canSeePayok && (
              <Badge color="grape" variant="light">
                {t('apiActivity.upstreamRowsVisible')}
              </Badge>
            )}
            {hasSynthesised && (
              <Tooltip label={t('apiActivity.reconstructedTooltip')}>
                <Badge color="yellow" variant="light">{t('apiActivity.reconstructedBadge')}</Badge>
              </Tooltip>
            )}
            <Text size="xs" c="dimmed">
              {t('apiActivity.entryCount', { n: data.rows.length })}
            </Text>
          </Group>
        )}

        {isLoading && <Center py="md"><Loader size="sm" /></Center>}

        {Boolean(error) && !(error instanceof ApiCallError && error.code === 'permission_denied') && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {describeError(error, t('apiActivity.loadFailed'))}
          </Alert>
        )}

        {data && data.rows.length === 0 && (
          <Text c="dimmed" ta="center" py="md">
            {t('apiActivity.emptyDeposit')}
          </Text>
        )}

        {lifecycle && (lifecycle.initial.merchantIn || lifecycle.initial.payokOut) && (
          <InitialRequestCard
            merchantIn={lifecycle.initial.merchantIn}
            payokOut={lifecycle.initial.payokOut}
            firstStartedAt={firstStartedAt}
            canSeePayok={canSeePayok}
          />
        )}

        {lifecycle && lifecycle.cycles.length > 0 && (
          <Stack gap="sm">
            <Text fw={600} size="sm" tt="uppercase" c="dimmed">
              {canSeePayok ? t('apiActivity.notificationCycles') : t('apiActivity.webhookNotifications')} ·{' '}
              {lifecycle.cycles.length}
            </Text>
            {lifecycle.cycles.map((cycle, idx) => (
              <CycleCard
                key={cycle.callback?.id ?? `delivery-${idx}`}
                cycle={cycle}
                index={idx + 1}
                firstStartedAt={firstStartedAt}
                canSeePayok={canSeePayok}
              />
            ))}
          </Stack>
        )}

        {lifecycle && lifecycle.orphans.length > 0 && (
          <Card withBorder padding="sm">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
              {t('apiActivity.orphanEntries')}
            </Text>
            <Accordion variant="default" multiple chevronPosition="left">
              {lifecycle.orphans.map((entry, idx) => (
                <TimelineEntry
                  key={entry.id}
                  entry={entry}
                  index={idx + 1}
                  total={lifecycle.orphans.length}
                  itemNoun={t('apiActivity.entryNoun')}
                  groupColor="gray"
                  firstStartedAt={firstStartedAt}
                />
              ))}
            </Accordion>
          </Card>
        )}
      </Stack>
    </Card>
  );
}

function minStartedAt(rows: readonly ApiAuditEntry[]): string {
  let min = rows[0]!.startedAt;
  for (const r of rows) if (r.startedAt < min) min = r.startedAt;
  return min;
}

/**
 * Initial Request card: the first time the merchant called us + (for internal
 * viewers) our outbound to the upstream provider to create the order.
 *
 * For external viewers `payokOut` is always null (server filters it out and
 * the lifecycle organiser also masks it), so the card collapses to the single
 * cashin row.
 */
function InitialRequestCard({
  merchantIn,
  payokOut,
  firstStartedAt,
  canSeePayok,
}: {
  merchantIn: ApiAuditEntry | null;
  payokOut: ApiAuditEntry | null;
  firstStartedAt: string | null;
  canSeePayok: boolean;
}) {
  const { t } = useTranslation();
  const subtitle = canSeePayok
    ? t('apiActivity.initialSubtitleInternalDeposit')
    : t('apiActivity.initialSubtitleExternalDeposit');
  return (
    <Card withBorder padding={0} radius="md">
      <Group
        justify="space-between"
        px="md"
        py="sm"
        style={{
          // light-dark() picks per scheme so the tinted header band stays
          // legible in both modes (was unreadable in dark before).
          background:
            'light-dark(var(--mantine-color-brand-0), var(--mantine-color-brand-9))',
          borderBottom:
            '1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-4))',
        }}
      >
        <Group gap="sm">
          <Badge color="brand" variant="filled" size="lg" radius="sm">
            {t('apiActivity.initialRequest')}
          </Badge>
          <Text size="sm" c="dimmed">{subtitle}</Text>
        </Group>
      </Group>
      <Accordion variant="default" multiple chevronPosition="left">
        {merchantIn && (
          <TimelineEntry
            entry={merchantIn}
            index={1}
            total={canSeePayok && payokOut ? 2 : 1}
            itemNoun={t('apiActivity.labelMerchantApiRequestNoun')}
            groupColor="brand"
            firstStartedAt={firstStartedAt}
            labelOverride={t('apiActivity.labelMerchantApiRequest')}
          />
        )}
        {canSeePayok && payokOut && (
          <TimelineEntry
            entry={payokOut}
            index={merchantIn ? 2 : 1}
            total={(merchantIn ? 1 : 0) + 1}
            itemNoun={t('apiActivity.labelUpstreamOrderCreateNoun')}
            groupColor="grape"
            firstStartedAt={firstStartedAt}
            labelOverride={t('apiActivity.labelUpstreamOrderCreate')}
          />
        )}
      </Accordion>
    </Card>
  );
}

/**
 * One round of activity for this deposit.
 *
 *  - For internal viewers (canSeePayok=true): the round is anchored by the
 *    upstream callback, with the downstream merchant-webhook attempts inside.
 *  - For external (merchant) viewers (canSeePayok=false): the upstream side
 *    is hidden; the card is just "Notification #N" containing the webhook
 *    attempts for that delivery-id. Merchant never sees the upstream entity.
 */
function CycleCard({
  cycle,
  index,
  firstStartedAt,
  canSeePayok,
}: {
  cycle: { label: string; callback: ApiAuditEntry | null; webhookAttempts: ApiAuditEntry[] };
  index: number;
  firstStartedAt: string | null;
  canSeePayok: boolean;
}) {
  const { t } = useTranslation();
  // Worst status across this cycle drives the header badge.
  const allEntries = [
    ...(cycle.callback ? [cycle.callback] : []),
    ...cycle.webhookAttempts,
  ];
  const summary = (() => {
    const hasErr = allEntries.some((e) => e.error != null);
    if (hasErr) return { color: 'red', label: t('apiActivity.summaryErrors') };
    const worst = allEntries.length > 0
      ? Math.max(...allEntries.map((e) => e.responseStatus ?? 0))
      : 0;
    const color = worst >= 500 ? 'red' : worst >= 400 ? 'orange' : worst >= 300 ? 'yellow' : 'green';
    return { color, label: t('apiActivity.summaryWorstHttp', { n: worst }) };
  })();
  const totalCount = allEntries.length;
  // Theme-aware header background. light-dark() picks 0-shade in light mode
  // and 9-shade in dark mode so the tinted band reads in both themes.
  const headerBg = canSeePayok
    ? 'light-dark(var(--mantine-color-grape-0), var(--mantine-color-grape-9))'
    : 'light-dark(var(--mantine-color-brand-0), var(--mantine-color-brand-9))';
  const headerBadgeColor = canSeePayok ? 'grape' : 'brand';
  const headerTitle = canSeePayok ? t('apiActivity.cycleTitle', { n: index }) : t('apiActivity.notificationTitle', { n: index });
  const headerSubtitle = canSeePayok
    ? (cycle.webhookAttempts.length === 1
        ? t('apiActivity.cycleSubtitleOne', { n: cycle.webhookAttempts.length })
        : t('apiActivity.cycleSubtitleMany', { n: cycle.webhookAttempts.length }))
    : (cycle.webhookAttempts.length === 1
        ? t('apiActivity.notificationSubtitleOne', { n: cycle.webhookAttempts.length })
        : t('apiActivity.notificationSubtitleMany', { n: cycle.webhookAttempts.length }));

  return (
    <Card withBorder padding={0} radius="md">
      <Group
        justify="space-between"
        px="md"
        py="sm"
        style={{
          background: headerBg,
          borderBottom:
            '1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-4))',
        }}
      >
        <Group gap="sm">
          <Badge color={headerBadgeColor} variant="filled" size="lg" radius="sm">
            {headerTitle}
          </Badge>
          <Text size="sm" c="dimmed">{headerSubtitle}</Text>
        </Group>
        <Group gap={6}>
          <Badge color={summary.color} variant="light">{summary.label}</Badge>
          <Text size="xs" c="dimmed">{t('apiActivity.cycleEntries', { n: totalCount })}</Text>
        </Group>
      </Group>
      <Accordion variant="default" multiple chevronPosition="left">
        {/* Upstream callback row (internal only). */}
        {canSeePayok && cycle.callback && (
          <TimelineEntry
            entry={cycle.callback}
            index={1}
            total={totalCount}
            itemNoun={t('apiActivity.upstreamCallbackNoun')}
            groupColor="grape"
            firstStartedAt={firstStartedAt}
            labelOverride={t('apiActivity.upstreamCallbackLabel')}
          />
        )}
        {/* Merchant webhook attempts (visible to both). */}
        {cycle.webhookAttempts.map((wh, idx) => (
          <TimelineEntry
            key={wh.id}
            entry={wh}
            index={idx + 1 + (canSeePayok && cycle.callback ? 1 : 0)}
            total={totalCount}
            itemNoun={t('apiActivity.webhookAttemptNoun', { n: idx + 1 })}
            groupColor="brand"
            firstStartedAt={firstStartedAt}
            labelOverride={t('apiActivity.webhookAttemptLabel', { n: idx + 1 })}
          />
        ))}
      </Accordion>
    </Card>
  );
}

function TimelineEntry({
  entry,
  index,
  total,
  itemNoun,
  groupColor,
  firstStartedAt,
  labelOverride,
}: {
  entry: ApiAuditEntry;
  index: number;
  total: number;
  itemNoun: string;
  groupColor: string;
  firstStartedAt: string | null;
  /** Human-readable role label shown on the row (e.g. "PAYOK → TransPay (Callback received)"). */
  labelOverride?: string;
}) {
  const { t } = useTranslation();
  const isInbound = entry.direction === 'inbound';
  const dirIcon = isInbound ? <IconArrowDown size={14} /> : <IconArrowUp size={14} />;

  const statusBadge = (() => {
    if (entry.error) return <Badge color="red" variant="filled" size="sm">{t('apiActivity.statusErr')}</Badge>;
    if (entry.responseStatus == null) return <Badge color="gray" variant="filled" size="sm">—</Badge>;
    const s = entry.responseStatus;
    const color = s >= 500 ? 'red' : s >= 400 ? 'orange' : s >= 300 ? 'yellow' : 'green';
    return <Badge color={color} variant="filled" size="sm">{s}</Badge>;
  })();

  // "+0.5s from start" relative timestamp helps grok the flow even when
  // attempts are minutes apart.
  const relative = (() => {
    if (!firstStartedAt) return '';
    const delta = new Date(entry.startedAt).getTime() - new Date(firstStartedAt).getTime();
    if (delta === 0) return 't+0s';
    if (delta < 1000) return `t+${delta}ms`;
    if (delta < 60_000) return `t+${(delta / 1000).toFixed(1)}s`;
    if (delta < 3600_000) return `t+${(delta / 60_000).toFixed(1)}min`;
    return `t+${(delta / 3600_000).toFixed(1)}h`;
  })();

  const dColor = durationColor(entry.durationMs);
  const responseTimeBadge = (
    <Tooltip
      label={
        isInbound
          ? t('apiActivity.durationTooltipInbound')
          : t('apiActivity.durationTooltipOutbound')
      }
    >
      <Badge color={dColor} variant="dot" size="md">
        {entry.durationMs != null ? `${entry.durationMs} ms` : t('apiActivity.noTiming')}
      </Badge>
    </Tooltip>
  );

  return (
    <Accordion.Item value={String(entry.id)}>
      <Accordion.Control>
        <Group gap="md" wrap="nowrap" align="center">
          <Badge color={groupColor} variant="outline" size="lg" radius="sm">
            #{index}{total > 1 ? ` / ${total}` : ''}
          </Badge>
          <Group gap={4} wrap="nowrap" style={{ minWidth: 150 }}>
            {dirIcon}
            <Text size="xs" c="dark" fw={500}>{labelOverride ?? itemNoun}</Text>
          </Group>
          <Text size="xs" ff="monospace" style={{ flex: 1, minWidth: 0 }} truncate c="dimmed">
            {entry.method} {entry.url}
          </Text>
          {entry.synthesised && (
            <Tooltip label={t('apiActivity.legacyTooltipDeposit')}>
              <Badge color="yellow" variant="dot" size="xs">{t('apiActivity.legacyBadge')}</Badge>
            </Tooltip>
          )}
          {statusBadge}
          {responseTimeBadge}
          <Text size="xs" c="dimmed" ff="monospace" style={{ minWidth: 60 }}>{relative}</Text>
          <Text size="xs" c="dimmed">{formatGmt7(entry.startedAt)}</Text>
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Stack gap="sm">
          {entry.error && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              {entry.error}
            </Alert>
          )}
          <Group gap="xl">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{t('apiActivity.started')}</Text>
              <Text size="xs" ff="monospace">{formatGmt7(entry.startedAt)}</Text>
            </div>
            {entry.completedAt && (
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{t('apiActivity.completed')}</Text>
                <Text size="xs" ff="monospace">{formatGmt7(entry.completedAt)}</Text>
              </div>
            )}
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{t('apiActivity.responseTime')}</Text>
              <Group gap={6}>
                <Text size="sm" ff="monospace" fw={600} c={`${durationColor(entry.durationMs)}.7`}>
                  {entry.durationMs != null ? `${entry.durationMs} ms` : '—'}
                </Text>
                {entry.responseStatus != null && (
                  <Text size="xs" c="dimmed">@ HTTP {entry.responseStatus}</Text>
                )}
              </Group>
            </div>
          </Group>
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                {t('apiActivity.request')}
              </Text>
              {entry.requestHeaders != null && (
                <>
                  <Text size="xs" c="dimmed">{t('apiActivity.headers')}</Text>
                  <JsonView value={entry.requestHeaders} maxHeight={160} />
                </>
              )}
              <Text size="xs" c="dimmed" mt={6}>{t('apiActivity.body')}</Text>
              <JsonView value={entry.requestBody} maxHeight={300} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                {t('apiActivity.response')}{' '}
                {entry.responseStatus != null && (
                  <Text span size="xs" c="dimmed" ff="monospace">
                    HTTP {entry.responseStatus}
                  </Text>
                )}
              </Text>
              {entry.responseHeaders != null && (
                <>
                  <Text size="xs" c="dimmed">{t('apiActivity.headers')}</Text>
                  <JsonView value={entry.responseHeaders} maxHeight={160} />
                </>
              )}
              <Text size="xs" c="dimmed" mt={6}>{t('apiActivity.body')}</Text>
              <JsonView value={entry.responseBody} maxHeight={300} />
            </Grid.Col>
          </Grid>
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
}
